import type { GoldenArtifactRun } from "../services/goldenArtifactsService";

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Allocates deterministic per-call subdirectories (`apply_00`, `apply_01`, ...)
 * for golden-artifact capture of each mutating tool call within a single turn.
 *
 * Each returned closure owns its own counter, so callers create one allocator
 * per turn/run. Shared by the deck-build flow (`runAgenticDeckBuild`) and the
 * chat flow (`useQueryHandler`) so both label apply_edits captures identically.
 */
export function createApplyArtifactSubdirectoryAllocator(): () => string {
  let count = 0;
  return () => `apply_${String(count++).padStart(2, "0")}`;
}

function sanitizeToolNameForSubdirectory(tool: string): string {
  const sanitized = tool
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return sanitized.length > 0 ? sanitized.slice(0, 40) : "tool";
}

/**
 * Allocates deterministic per-tool subdirectories for every native tool call in
 * a turn. Mutating apply_edits calls keep the established `apply_NN` shape, while
 * observe/data tools use `tool_NN_<tool>` folders so their call/output artifacts
 * are easy to scan in chronological order.
 *
 * An optional `prefix` namespaces every subdirectory (e.g. `polish_s01_apply_00`),
 * which the post-build polish phase uses to keep each per-slide run's artifacts
 * collision-free under the single deck-level run folder. Subdirectories are a
 * single flat segment (the server rejects path separators), so the prefix is
 * joined with `_` and the whole name is clamped to the server's 64-char limit.
 */
export function createToolArtifactSubdirectoryAllocator(
  prefix?: string
): (tool: string) => string {
  let toolCount = 0;
  const nextApplySubdirectory = createApplyArtifactSubdirectoryAllocator();
  const cleanPrefix = prefix ? sanitizeToolNameForSubdirectory(prefix) : "";
  const withPrefix = (name: string): string =>
    (cleanPrefix ? `${cleanPrefix}_${name}` : name).slice(0, 64);
  return (tool: string) => {
    if (tool === "apply_edits") {
      return withPrefix(nextApplySubdirectory());
    }
    const label = `tool_${String(toolCount++).padStart(2, "0")}_${sanitizeToolNameForSubdirectory(
      tool
    )}`;
    return withPrefix(label);
  };
}

/**
 * Failure-tolerant wrapper around a single golden-artifact upload step.
 *
 * - If `run` is `null` the operation is skipped entirely (caller can pass a
 *   conditional run without an outer `if` block).
 * - Any error thrown by `operation` is caught, logged, and swallowed so a
 *   golden-capture failure never aborts the caller's flow (chat turn or deck
 *   creation). Uploads are best-effort instrumentation, not part of the
 *   user-visible contract.
 *
 * Originally inlined in `useQueryHandler.ts`; lifted here so the deck-creation
 * pipeline (`useDeckCreation`, `generateSlideContent`) can reuse the same
 * swallow-and-log contract without duplicating the helper.
 */
export async function runGoldenCaptureStep(
  run: GoldenArtifactRun | null,
  log: (message: string) => void,
  label: string,
  operation: () => Promise<void> | void
): Promise<void> {
  if (!run) {
    return;
  }
  try {
    await operation();
    log(`[GOLDEN] Saved ${label} (${run.runId})`);
  } catch (error) {
    log(`[GOLDEN] Failed ${label} (${run.runId}): ${toErrorMessage(error)}`);
  }
}
